import React from 'react';
import styled from 'styled-components';
import Button from '../components/Button';
import PageHeroSection from '../components/hero-section/HomeHeroSection';
import IntellicubixBinaryImage from '../assets/general/intellicubix-binary.png';
import AkpromediaLogo from '../assets/clients/akpromedia-logo.webp';
import ContentHubLogo from '../assets/clients/content-hub-logo.webp';
import EmeraldLogo from '../assets/clients/emerald-logo.webp';
import EncryptDataTransferLogo from '../assets/clients/encrypt-data-transfer-logo.webp';
import FinancialWiseGuidanceLogo from '../assets/clients/financial-wise-guidance-logo.webp';
import NutsinboxLogo from '../assets/clients/nutsinbox-logo.webp';
import ZilpLogo from '../assets/clients/zilp-logo.webp';
import ZinstantLogo from '../assets/clients/zinstant-logo.webp';


const MainContainer = styled.div`
  width: 100%;
  min-height: calc(100vh - 200px);
`;

const SectionTitle = styled.h2`
  font-size: 34px;
  font-weight: 500;
  color: ${(props) => props.theme.colors.white};
  font-family: ${(props) => props.theme.fonts.headline};
  text-align: center;
  margin: 20px 0;
  text-shadow: 1px 1px 3px rgba(0, 0, 0, .5);

  span {
    color: ${(props) => props.theme.colors.magenta};
    font-weight: 700;
  }

  &.partner-section {
    margin-top: 60px;
  }
`;

const SectionContainer = styled.div`
  width: 100%;
  max-width: 1170px;
  margin: 0 auto;
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;

  &.service-section {
    margin-bottom: -35px;
  }
`;

const SectionLeft = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;

  @media (max-width: ${({ theme }) => theme.screens.tablet}) {
    &.section-image {
      display: none;
    }
  }

`;

const SectionRight = styled.div`

  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;

  @media (max-width: ${({ theme }) => theme.screens.tablet}) {
    &.section-image {
      display: none;
    }
  }
`;

const SubSectionTitle = styled.h3`
  font-size: 26px;
  font-weight: 500;
  color: ${(props) => props.theme.colors.navy};
  font-family: ${(props) => props.theme.fonts.headline};
  text-align: justify;
  margin-bottom: 20px;

  span {
    color: ${(props) => props.theme.colors.magenta};
    font-weight: 700;
  }

  &.service-title {
    font-weight: 700;
    margin-bottom: 10px;
  }

  @media (max-width: ${({ theme }) => theme.screens.tablet}) {
    text-align: left;
  }
`;

const SubSectionDescription = styled.p`
  font-size: 22px;
  font-weight: 400;
  color: ${(props) => props.theme.colors.white};
  font-family: ${(props) => props.theme.fonts.content};
  text-align: justify;
  line-height: 1.3;
  margin-bottom: 20px;

  span {
    font-weight: 700;
    color: ${(props) => props.theme.colors.magenta};
  }
`;

const SubSectionImage = styled.img`
  width: 140px;
  height: auto;
  max-height: 500px;

  @media (max-width: ${({ theme }) => theme.screens.tablet}) {
    display: none;
  }
`;

const PartnerSection = styled.div`
  width: 100%;
  max-width: 1170px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 10px; // row and column gap to provide spacing between images
  justify-items: center; // center images within their grid cell
`;

const PartnerImage = styled.img`
  width: 260px;
  height: 260px;
`;

const Home = () => {
  return (
    <MainContainer>
      <PageHeroSection />
      <SectionTitle>
        <span>Unleash Brilliance:</span> Crafting Products with Purpose and Passion
      </SectionTitle>
      <SectionContainer>
        <SectionLeft>
          <SubSectionTitle>
            Launch Into Your Innovation Odyssey with <span>IntelliCubix</span>
          </SubSectionTitle>
          <SubSectionDescription>
            Dive into the heart of innovation with <span>IntelliCubix</span>, your dedicated ally in the quest to create not just products, but legacies. With a treasure trove of over a decade's worth of expertise in sculpting success stories, we stand ready to breathe life into your vision. Whether you're the seed of a startup or the driving force of a sprawling enterprise, your mission becomes our quest.
          </SubSectionDescription>
          <SubSectionDescription>
            <span>IntelliCubix</span> is more than a service; it's a partnership where dreams and design converge. We bring a bespoke, design-forward approach to the table, tackling challenges with a blend of creativity and precision that's as unique as you are. Our compact yet potent team of product maestros is tailor-made to align with the contours of your project, ensuring a journey as smooth as it is transformative.
          </SubSectionDescription>
          <SubSectionDescription>
            Let's transform ideas into impact together. With <span>IntelliCubix</span>, your venture isn't just built; it's envisioned, crafted, and nurtured to thrive.
          </SubSectionDescription>
          <Button text="LET’S WORK TOGETHER" />
        </SectionLeft>
        <SectionRight className="section-image">
          <SubSectionImage src={IntellicubixBinaryImage} alt="Intellicubix Binary Image" />
        </SectionRight>
      </SectionContainer>

      <SectionTitle>
        <span>Unlock the Power of Innovation:</span> Our Expert Services
      </SectionTitle>

      <SectionContainer className="service-section">
        <SectionLeft className="section-image">
          <SubSectionImage src={IntellicubixBinaryImage} alt="Intellicubix Binary Image" />
        </SectionLeft>
        <SectionRight>
          <SubSectionTitle className="service-title">
            Web and Mobile App Development
          </SubSectionTitle>
          <SubSectionDescription>
            Dive into the digital age with custom solutions that speak volumes. At <span>IntelliCubix</span>, we sculpt web and mobile applications that are not just functional but a masterpiece of your vision brought to life. Our applications are designed to captivate, connect, and convert. Whether it’s launching a startup app that goes viral or scaling an enterprise solution, our creative architects ensure your digital presence is nothing short of extraordinary.
          </SubSectionDescription>
          <SubSectionTitle className="service-title">
            Data-Driven Software Development
          </SubSectionTitle>
          <SubSectionDescription>
            Elevate your operations with software that’s smart, agile, and ahead of the curve. <span>IntelliCubix</span> specializes in developing software solutions that adapt and grow with your business. By leveraging the power of data, we ensure that your software not only meets today's needs but anticipates tomorrow's challenges. Experience the synergy of innovation and intelligence, and watch your business soar.
          </SubSectionDescription>
          <SubSectionTitle className="service-title">
            Data Analysis and Machine Learning Modeling
          </SubSectionTitle>
          <SubSectionDescription>
            Harness the potential of your data with <span>IntelliCubix</span>'s cutting-edge analysis and machine learning models. We transform complex datasets into clear insights and actionable strategies. By predicting trends and optimizing processes, our tailored models empower you to lead with confidence. Step into the future of decision-making, where every choice is backed by data-driven brilliance.
          </SubSectionDescription>
        </SectionRight>
      </SectionContainer>
      <Button text="LET’S WORK TOGETHER" />

      <SectionTitle className="partner-section">
        <span>Trusted by Industry Leaders:</span> Our Esteemed Partners
      </SectionTitle>
      <PartnerSection>
        <PartnerImage src={EncryptDataTransferLogo} alt="Encrypt Data Transfer Logo" />
        <PartnerImage src={EmeraldLogo} alt="Emerald Logo" />
        <PartnerImage src={ZinstantLogo} alt="Zinstant Logo" />
        <PartnerImage src={FinancialWiseGuidanceLogo} alt="Financial Wise Guidance Logo" />
        <PartnerImage src={AkpromediaLogo} alt="Akpromedia Logo" />
        <PartnerImage src={ContentHubLogo} alt="Content Hub Logo" />
        <PartnerImage src={ZilpLogo} alt="Zilp Logo" />
        <PartnerImage src={NutsinboxLogo} alt="Nutsinbox Logo" />
      </PartnerSection>
      <Button text="LET’S WORK TOGETHER" />
    </MainContainer>
  );
};

export default Home;

